<template>
    <transition name="cart">
        <div id="popular-addons" class="card has-footer">
            <h1 class="font-regular"> {{ lang('Populära tillval','Popular addons') }}</h1>

            <twin-setting
                v-if="showTwin"
                v-model="selectedTwinCampaign"
                :twin="twin"
                :twin-campaigns="twin.campaigns"
                :hard-drive="hardDrive"
                @hardDriveSelected="(newValue) => hardDriveSelected = newValue"
                @twinSelected="(newValue) => wantsToOrderTwin = newValue"
            />

            <tv-packages-setting
                v-if="availableTvPackages.length > 0"
                v-model="selectedTvPackages"
                :tv-packages="availableTvPackages"
            />

            <tv-channels-setting
                v-if="availableTvChannels.length > 0"
                v-model="selectedTvChannels"
                :tv-channels="availableTvChannels"
            />

            <configuration-footer
                :validates="canAddToCart"
                :monthly-price="monthlyPrice"
                :one-time-cost="oneTimeCost"
                @add="addToCart"
            />
        </div>
    </transition>
</template>

<script>
    import ConfigurationFooter from '../ConfigurationFooter';
    import TwinSetting from './Settings/TwinSetting';
    import TvPackagesSetting from './Settings/TvPackagesSetting';
    import TvChannelsSetting from './Settings/TvChannelsSetting';

    export default {
        components: {
            ConfigurationFooter,
            TwinSetting,
            TvPackagesSetting,
            TvChannelsSetting,
        },
        props: {
            showTwin: {
                required: true,
                type: Boolean,
            },
            twin: {
                type: Object,
                required: true,
            },
            availableTvPackages: {
                type: Array,
                required: true,
            },
            availableTvChannels: {
                type: Array,
                required: true,
            },
            hardDrive: {
                type: Object,
                required: false,
                default: null,
            },
        },
        data() {
            return {
                hardDriveSelected: false,
                selectedTvChannelPackages: [],
                selectedTvPackages: [],
                selectedTvChannels: [],
                selectedTwinCampaign: null,
                wantsToOrderTwin: false,
            };
        },
        computed: {
            twinSelected() {
                if (this.wantsToOrderTwin && ! this.twin.campaigns.length) {
                    return true;
                }

                return !! this.selectedTwinCampaign;
            },
            canAddToCart() {
                // If user wants to order a Twin, a selected campaign (ie. for the hd box) must be selected
                // UNLESS the twin package is collective
                if (this.wantsToOrderTwin && (this.selectedTwinCampaign === null && ! this.twin.is_collective)) {
                    return false;
                }

                return this.selectedTvPackages.length > 0
                    || this.selectedTvChannels.length > 0
                    || this.twinSelected;
            },
            monthlyPrice() {
                const tvPackagesCost = this.selectedTvPackageObjects.reduce((prev, next) => {
                    return prev + next.monthly_price;
                }, 0);

                const tvChannelsCost = this.selectedTvChannelObjects.reduce((prev, next) => {
                    return prev + next.monthly_price;
                }, 0);

                let twinCost = 0;
                if (this.selectedTwinCampaignObject) {
                    twinCost = this.selectedTwinCampaignObject.monthly_price;
                }

                return format_sek_per_month(tvPackagesCost + tvChannelsCost + twinCost);
            },
            oneTimeCost() {
                // TODO: A TV-package can in theory have start fees. We should
                // build to account for that just in case they decide to start
                // putting start fees on packages.
                let oneTimeCost = 0;

                if (this.selectedTwinCampaignObject) {
                    oneTimeCost = this.selectedTwinCampaignObject.start_fee;
                }

                if (this.hardDriveSelected) {
                    oneTimeCost += this.hardDrive.price;
                }

                return format_sek(oneTimeCost);
            },
            selectedTvPackageObjects() {
                return this.selectedTvPackages.map(
                    (articleId) => this.availableTvPackages.find(
                        (tvPackage) => tvPackage.article_id === articleId
                    )
                );
            },
            selectedTwinCampaignObject() {
                return this.twin.campaigns.find((campaign) => {
                    return campaign.article_id === this.selectedTwinCampaign;
                });
            },
            selectedTvChannelObjects() {
                return this.selectedTvChannels.map(
                    (articleId) => this.availableTvChannels.find(
                        (tvPackage) => tvPackage.article_id === articleId
                    )
                );
            },
        },
        methods: {
            async addToCart() {
                const articles = [];

                this.selectedTvPackageObjects.forEach((tvPackage) => {
                    articles.push({
                        articleClass: tvPackage.article_class,
                        articleId: tvPackage.article_id,
                    });
                });

                if (this.twinSelected) {
                    // A non-collective twin package always has campaigns for the hd box
                    if (this.twin.campaigns.length) {
                        const selectedCampaign = this.twin.campaigns.find((campaign) => {
                            return campaign.article_id === this.selectedTwinCampaign;
                        });

                        const twinCampaignArticle = {
                            articleClass: selectedCampaign.article_class,
                            articleId: selectedCampaign.article_id,
                            subItems: {},
                            parameters: {
                                hardDriveSelected: this.hardDriveSelected,
                            },
                        };


                        const hdBox = selectedCampaign.hd_boxes.length > 0 ? selectedCampaign.hd_boxes[0] : null;
                        if (hdBox) {
                            twinCampaignArticle['subItems']['hdBox'] = {
                                articleClass: hdBox.article_class,
                                articleId: hdBox.article_id,
                            };
                        }

                        articles.push(twinCampaignArticle);
                    }

                    // Twin can be collective so in this case just add the package
                    if (this.twin.is_collective) {
                        articles.push({
                            articleClass: this.twin.article_class,
                            articleId: this.twin.article_id,
                            parameters: {
                                hardDriveSelected: this.hardDriveSelected,
                            },
                        });
                    }
                }

                this.selectedTvChannelObjects.forEach((tvChannel) => {
                    articles.push({
                        articleClass: tvChannel.article_class,
                        articleId: tvChannel.article_id,
                    });
                });

                const response = await axios.post(route('front.api.cart-items.store'), {
                    articles,
                });

                EventBus.$emit('cartDataUpdated', response.data, 'Tillval');
                this.productsAddedToCart = true;
                this.$emit('completed');
            },
        },
    };
</script>
